import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { getEkycStatusLabel, getPkycStatusLabel, EKYCStatus, PKYCStatus } from '../constants/kycStatus';
import CreatableSelect from 'react-select/creatable';
import { api, apiMethods } from '../services/api';
import { playSuccessSound, playErrorSound } from '../utils/sound';
import { useAuth } from '../hooks/useAuth';

interface AddQueryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  commonQueries: { query: string; response: string }[];
}

interface UserInfo {
  name: string;
  type: string;
  state: string;
  city: string;
  'E-KYC_Status'?: EKYCStatus;
  'P-KYC_Status'?: PKYCStatus;
}

const AddQueryModal: React.FC<AddQueryModalProps> = ({ isOpen, onClose, onSuccess, commonQueries }) => {
  const [mobileNumber, setMobileNumber] = useState('');
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [selectedQuery, setSelectedQuery] = useState('');
  const [response, setResponse] = useState('');
  const [status, setStatus] = useState('pending');
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [manualName, setManualName] = useState('');
  const [manualType, setManualType] = useState('');
  const [manualCity, setManualCity] = useState('');
  const [showManualOverride, setShowManualOverride] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMobileNumber('');
      setUserInfo(null);
      setSelectedQuery('');
      setResponse('');
      setStatus('pending');
      setRemarks('');
      setIsNewUser(false);
      setManualName('');
      setManualType('');
      setManualCity('');
      setShowManualOverride(false);
    }
    }, [isOpen]);

  // Auto-fetch when mobile number reaches 10 digits
  useEffect(() => {
    if (mobileNumber.length === 10 && /^\d{10}$/.test(mobileNumber)) {
      fetchUserInfo();
    }
  }, [mobileNumber]);

  console.log('AddQueryModal rendered!');

  // Handle mobile number input with validation
  const handleMobileNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numbers and limit to 10 digits
    const numbersOnly = value.replace(/[^0-9]/g, '');
    if (numbersOnly.length <= 10) {
      setMobileNumber(numbersOnly);
      // Reset user info when mobile number changes
      if (numbersOnly.length < 10) {
        setUserInfo(null);
        setIsNewUser(false);
      }
    }
  };

  const fetchUserInfo = async () => {
    if (mobileNumber.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      playErrorSound();
      return;
    }

    setLoading(true);
    setIsNewUser(false); // Reset on every fetch
    try {
      const response: any = await apiMethods.post(
        '/proxy/member-details',
        { mobile_number: mobileNumber }
      );
      if (response && response.data) {
        setUserInfo({
          name: response.data.UserName,
          type: response.data.UserType,
          state: response.data.state,
          city: response.data.city,
          'E-KYC_Status': response.data['E-KYC_Status'],
          'P-KYC_Status': response.data['P-KYC_Status'],
        });
      } else {
        // Handle case where API returns success but no data
        setUserInfo(null);
        setIsNewUser(true);
        setStatus('enquiry');
        toast.error('User not found. Please fill in the new user details below.');
        playErrorSound();
      }
    } catch (error) {
      toast.error('User not found. Please fill in the new user details below.');
      playErrorSound();
      setUserInfo(null);
      setIsNewUser(true); // Show new user fields on error
      setStatus('enquiry'); // Set status to enquiry for new users
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    // Validation checks
    if (!userInfo && !isNewUser) {
      toast.error('Please fetch user information first or click "New User"');
      playErrorSound();
      return;
    }

    const finalUserName = isNewUser ? manualName : (showManualOverride && manualName.trim() ? manualName : userInfo?.name);
    const finalUserType = isNewUser ? manualType : (showManualOverride && manualType.trim() ? manualType : userInfo?.type);
    const finalCity = isNewUser ? manualCity : (showManualOverride && manualCity.trim() ? manualCity : userInfo?.city);

    if (!finalUserName || !finalUserName.trim()) {
      toast.error('User name is required');
      playErrorSound();
      return;
    }
    if (!finalUserType || !finalUserType.trim()) {
      toast.error('User type is required');
      playErrorSound();
      return;
    }
    if (!finalCity || !finalCity.trim()) {
      toast.error('City is required');
      playErrorSound();
      return;
    }
    if (!selectedQuery.trim()) {
      toast.error('Query is required');
      playErrorSound();
      return;
    }
    if (!response.trim()) {
      toast.error('Response is required');
      playErrorSound();
      return;
    }

    setLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      const newQueryData = {
        userMobileNumber: mobileNumber,
        userName: finalUserName.trim(),
        userType: finalUserType.trim(),
        city: finalCity.trim(),
        ekycStatus: isNewUser ? 'NA' : (userInfo?.['E-KYC_Status'] || 'NA'),
        pkycStatus: isNewUser ? 'NA' : (userInfo?.['P-KYC_Status'] || 'NA'),
        query: selectedQuery.trim(),
        response: response.trim(),
        status,
        remarks: remarks.trim(),
      };
      await apiMethods.post('/queries', newQueryData);
      toast.success('Query added successfully!');
      playSuccessSound();
      onSuccess();
      onClose();
      setIsNewUser(false);
      setManualName('');
      setManualType('');
      setManualCity('');
      setShowManualOverride(false);
    } catch (error: any) {
      if (error.response && error.response.data && error.response.data.message) {
        toast.error(`Error: ${error.response.data.message}`);
        playErrorSound();
      } else {
        toast.error('An unexpected error occurred.');
        playErrorSound();
      }
    } finally {
      setLoading(false);
    }
  };

  const queryOptions = commonQueries.map(q => ({ label: q.query, value: q.query }));
  const responsesMap: Record<string, string> = Object.fromEntries(commonQueries.map(q => [q.query, q.response]));

  const handleQueryChange = (option: any) => {
    if (option && option.__isNew__) {
      setSelectedQuery(option.value);
      setResponse(''); // manual response
    } else if (option) {
      setSelectedQuery(option.value);
      // auto-fill response if found in commonQueries
      const found = responsesMap[option.value];
      setResponse(found || '');
    } else {
      setSelectedQuery('');
      setResponse('');
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg w-full max-w-5xl min-w-[900px] max-h-[90vh] flex flex-col">
          {/* Fixed Header */}
          <div className="flex justify-between items-center p-6 pb-4 border-b">
            <Dialog.Title className="text-lg font-medium">Add New Query</Dialog.Title>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          
          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-6 pt-4" style={{ maxHeight: 'calc(90vh - 140px)' }}>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Mobile Number</label>
                  <div className="mt-1 flex">
                    <input
                      type="tel"
                      value={mobileNumber}
                      onChange={handleMobileNumberChange}
                      className="input-field rounded-r-none"
                      placeholder="Enter 10-digit mobile number"
                      maxLength={10}
                      pattern="[0-9]{10}"
                      required
                    />
                    <button
                      type="button"
                      onClick={fetchUserInfo}
                      disabled={loading || mobileNumber.length !== 10}
                      className="btn-primary rounded-l-none"
                    >
                      Fetch
                    </button>
                  </div>
                  <div className="min-h-[16px] mt-1">
                    {mobileNumber.length > 0 && mobileNumber.length < 10 && (
                      <p className="text-xs text-gray-500">
                        {10 - mobileNumber.length} more digits needed
                      </p>
                    )}
                  </div>
                </div>
                <div className="self-start pt-6">
                  {/* "New User" button removed. Form will appear automatically on not found. */}
                </div>
              </div>
              {userInfo && (
                <div className="bg-gray-50 p-3 rounded-md">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-sm font-medium text-gray-700">User Information</h3>
                    {(!userInfo.name || !userInfo.type || !userInfo.city) && (
                      <button 
                        type="button" 
                        className="btn-secondary text-xs"
                        onClick={() => setShowManualOverride(!showManualOverride)}
                      >
                        {showManualOverride ? 'Hide' : 'Fix Missing Fields'}
                      </button>
                    )}
                  </div>
                  
                  {/* Warning for missing required fields */}
                  {(!userInfo.name || !userInfo.type || !userInfo.city) && (
                    <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                      <p className="text-xs text-yellow-800">
                        ⚠️ Warning: Some required fields are missing from user data.
                      </p>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-3 gap-x-6 text-xs">
                    <div className="flex flex-col">
                      <div>
                        <span className="text-gray-500 font-medium">Name:</span>
                        <span className={`font-medium ml-1 ${!userInfo.name ? 'text-red-500' : ''}`}>
                          {userInfo.name || 'Missing'}
                        </span>
                      </div>
                      <div className="mt-1">
                        <span className="text-gray-500 font-medium">Type:</span>
                        <span className={`font-medium ml-1 ${!userInfo.type ? 'text-red-500' : ''}`}>
                          {userInfo.type || 'Missing'}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col">
                      <div>
                        <span className="text-gray-500 font-medium">State:</span>
                        <span className="font-medium ml-1">{userInfo.state || '-'}</span>
                      </div>
                      <div className="mt-1">
                        <span className="text-gray-500 font-medium">City:</span>
                        <span className={`font-medium ml-1 ${!userInfo.city ? 'text-red-500' : ''}`}>
                          {userInfo.city || 'Missing'}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col">
                      <div>
                        <span className="text-gray-500 font-medium">E-KYC:</span>
                        <span className="font-medium ml-1">{getEkycStatusLabel(userInfo['E-KYC_Status'] || 'notinitiated')}</span>
                      </div>
                      <div className="mt-1">
                        <span className="text-gray-500 font-medium">P-KYC:</span>
                        <span className="font-medium ml-1">{getPkycStatusLabel(userInfo['P-KYC_Status'] || 'notinitiated')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {userInfo && showManualOverride && (
                <div className="bg-blue-50 p-3 rounded-md">
                  <h3 className="text-sm font-medium text-blue-700 mb-2">Manual Override (Optional)</h3>
                  <div className="grid grid-cols-3 gap-x-4">
                    <div>
                      <label className="text-xs font-medium text-blue-700">Name Override</label>
                      <input 
                        value={manualName} 
                        onChange={e => setManualName(e.target.value)} 
                        className="input-field text-xs" 
                        placeholder={userInfo.name || 'Enter name'}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-blue-700">Type Override</label>
                      <input 
                        value={manualType} 
                        onChange={e => setManualType(e.target.value)} 
                        className="input-field text-xs" 
                        placeholder={userInfo.type || 'Enter type'}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-blue-700">City Override</label>
                      <input 
                        value={manualCity} 
                        onChange={e => setManualCity(e.target.value)} 
                        className="input-field text-xs" 
                        placeholder={userInfo.city || 'Enter city'}
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {isNewUser && (
                <div className="bg-gray-50 p-3 rounded-md">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">New User Details</h3>
                  <div className="grid grid-cols-3 gap-x-4">
                    <div>
                      <label className="text-xs font-medium">Name</label>
                      <input value={manualName} onChange={e => setManualName(e.target.value)} className="input-field text-xs" required />
                    </div>
                    <div>
                      <label className="text-xs font-medium">Type</label>
                      <input value={manualType} onChange={e => setManualType(e.target.value)} className="input-field text-xs" required />
                    </div>
                    <div>
                      <label className="text-xs font-medium">City</label>
                      <input value={manualCity} onChange={e => setManualCity(e.target.value)} className="input-field text-xs" required />
                    </div>
                  </div>
                </div>
              )}
              {commonQueries.length === 0 && (
                <div className="text-xs text-red-500 mb-2">No query types available. Please add in QnR Management.</div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Query Type</label>
                <CreatableSelect
                  isClearable
                  options={queryOptions}
                  onChange={handleQueryChange}
                  value={selectedQuery ? { label: selectedQuery, value: selectedQuery } : null}
                  placeholder="Select or type a query"
                  classNamePrefix="react-select"
                  isDisabled={commonQueries.length === 0}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Response</label>
                <textarea
                  value={response}
                  onChange={(e) => setResponse(e.target.value)}
                  className="input-field mt-1"
                  rows={2}
                  required
                  disabled={!!responsesMap[selectedQuery] && response === responsesMap[selectedQuery]}
                  placeholder={selectedQuery && responsesMap[selectedQuery] ? 'Auto-filled' : 'Enter response'}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="input-field mt-1"
                    required
                  >
                    <option value="pending">Pending</option>
                    <option value="resolved">Resolved</option>
                    <option value="unsolved">Unsolved</option>
                    <option value="enquiry">Enquiry</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Remarks</label>
                  <textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    className="input-field mt-1"
                    rows={1}
                    placeholder="Optional remarks"
                  />
                </div>
              </div>
            </form>
          </div>
          
          {/* Fixed Footer */}
          <div className="border-t p-6 pt-4 bg-gray-50">
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary"
                onClick={handleSubmit}
              >
                {loading ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default AddQueryModal; 